import React from 'react';
import { useCart } from '../context/CartContext';
import { motion } from 'framer-motion';
import { ShoppingBag, ArrowRight, Trash2, Plus, Minus, Calendar, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

const Cart: React.FC = () => {
  const { cart, removeFromCart, addToCart } = useCart();

  const updateQuantity = (itemId: string, currentQty: number, delta: number) => {
    const newQty = Math.max(1, currentQty + delta);
    const diff = newQty - currentQty;
    if (diff !== 0) {
      const item = cart.find(i => i.id === itemId);
      if (item) addToCart({ ...item, quantity: diff });
    }
  };

  const TAX_RATE = 0.0675;
  const calculateSubtotal = () => cart.reduce((sum, item) => sum + (item.price * (item.quantity ?? 1)), 0);
  const calculateTax = () => calculateSubtotal() * TAX_RATE;
  const calculateTotal = () => calculateSubtotal() + calculateTax();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#FDF8F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="bg-white rounded-3xl shadow-lg p-12 text-center">
            <div className="w-20 h-20 bg-[#D4A5A5]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="w-10 h-10 text-[#D4A5A5]" />
            </div>
            <h1 className="font-serif text-3xl font-semibold text-[#3D3D3D] mb-4">Your Cart is Empty</h1>
            <p className="font-sans text-[#3D3D3D]/70 mb-8 max-w-md mx-auto">
              Looks like you haven't added any beautiful journaling pages or workshop tickets to your cart yet. Let's find something inspiring!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/products">
                <button className="bg-[#3D3D3D] hover:bg-[#2D2D2D] text-white px-6 py-3 rounded-full font-sans text-sm leading-none group inline-flex items-center justify-center whitespace-nowrap">
                  <span className="inline-block">Browse the Shop</span>
                  <ArrowRight className="ml-1.5 w-4 h-4 inline-block group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              <Link to="/events">
                <button className="border border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962]/10 px-6 py-3 rounded-full font-sans text-sm leading-none group inline-flex items-center justify-center whitespace-nowrap">
                  View Events
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F5] py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-4xl font-semibold text-[#3D3D3D] mb-8">Shopping Cart</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item, index) => (
              <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }} className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex flex-col sm:flex-row gap-6">
                  <div className="flex-shrink-0 w-full sm:w-auto">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full sm:w-24 h-40 sm:h-24 object-cover rounded-xl mb-3 sm:mb-0"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-serif text-lg font-semibold text-[#3D3D3D] mb-1">{item.title}</h3>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${item.type === 'event' ? 'bg-[#C9A962]/20 text-[#C9A962]' : 'bg-[#D4A5A5]/20 text-[#D4A5A5]'}`}>
                          {item.type === 'event' ? 'Workshop Ticket' : 'Digital Product'}
                        </span>
                      </div>
                      <button onClick={() => removeFromCart(item.id)} className="text-[#3D3D3D]/40 hover:text-red-500 transition-colors" aria-label={`Remove ${item.title} from cart`} title={`Remove ${item.title}`}>
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>

                    {item.type === 'event' && (
                      <div className="text-sm text-[#3D3D3D]/60 space-y-1 mb-3">
                        {item.date && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(item.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                            {item.time && <span>• {item.time}</span>}
                          </div>
                        )}
                        {item.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span>{item.location}</span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between gap-3 sm:flex-row">
                      <div className="flex items-center gap-3">
                        <button onClick={() => updateQuantity(item.id, item.quantity ?? 1, -1)} disabled={(item.quantity ?? 1) <= 1} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" aria-label={`Decrease quantity of ${item.title}`} title="Decrease">
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-sans font-medium text-[#3D3D3D] w-8 text-center">{item.quantity ?? 1}</span>
                        <button onClick={() => updateQuantity(item.id, item.quantity ?? 1, 1)} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors" aria-label={`Increase quantity of ${item.title}`} title="Increase">
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="text-right w-auto sm:w-auto">
                        <p className="font-serif text-xl font-semibold text-[#3D3D3D]">${((item.price ?? 0) * (item.quantity ?? 1)).toFixed(2)}</p>
                        {(item.quantity ?? 1) > 1 && <p className="text-sm text-[#3D3D3D]/50">${(item.price ?? 0).toFixed(2)} each</p>}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl p-8 shadow-lg sticky top-32">
              <h2 className="font-serif text-2xl font-semibold text-[#3D3D3D] mb-6">Order Summary</h2>
              <div className="space-y-3 mb-6 pb-6 border-b border-gray-100">
                <div className="flex justify-between font-sans text-[#3D3D3D]">
                  <span>Subtotal ({cart.length} item{cart.length > 1 ? 's' : ''})</span>
                  <span>${calculateSubtotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-sans text-[#3D3D3D]">
                  <span>Tax (6.75%)</span>
                  <span>${calculateTax().toFixed(2)}</span>
                </div>
              </div>
              <div className="flex justify-between font-serif text-2xl font-semibold text-[#3D3D3D] mb-8">
                <span>Total</span>
                <span>${calculateTotal().toFixed(2)}</span>
              </div>
              <Link to="/checkout">
                <button className="w-full bg-[#3D3D3D] hover:bg-[#2D2D2D] text-white py-6 rounded-full font-sans text-lg">Proceed to Checkout</button>
              </Link>
              <p className="text-center font-sans text-sm text-[#3D3D3D]/50 mt-2">Secure checkout • Instant delivery</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
